import type { AxiosError } from 'axios'

import kratos from '../requesters/kratos'

export interface KratosUiNode {
  attributes?: { name?: string; value?: string }
  meta?: { label?: { context?: { display_name?: string; added_at?: string } } }
}

interface KratosFlowResponse {
  id?: string
  ui?: { nodes?: KratosUiNode[] }
}

interface KratosLoginResponse {
  session?: { identity?: { traits?: { email?: string } } }
}

export const getNodeValue = (nodes: KratosUiNode[], name: string) =>
  nodes.find((node) => node?.attributes?.name === name)?.attributes?.value ?? ''

export const parsePasskeys = (nodes: KratosUiNode[]) =>
  nodes
    .filter((node) => node?.attributes?.name === 'passkey_remove')
    .map((node) => ({
      token: node?.attributes?.value ?? '',
      display_name: node.meta?.label?.context?.display_name ?? '',
      added_at: node.meta?.label?.context?.added_at ?? '',
    }))

// Accept: application/json makes Kratos return JSON instead of browser redirects
const fetchKratosFlow = async (path: string) => {
  const response = await kratos.get<KratosFlowResponse>(path, {
    params: { refresh: true },
    headers: { Accept: 'application/json' },
  })
  return response.data
}

// Kratos expects flow submissions form-urlencoded, not JSON
const submitKratosFlow = <T>(path: string, flowId: string, fields: Record<string, string>) =>
  kratos.post<T>(path, new URLSearchParams(fields), {
    params: { flow: flowId },
    headers: { Accept: 'application/json', 'Content-Type': 'application/x-www-form-urlencoded' },
  })

export const fetchKratosLoginFlow = async (): Promise<KratosLoginFlow> => {
  const flow = await fetchKratosFlow('/self-service/login/browser')
  const nodes = flow?.ui?.nodes || []

  return {
    id: flow.id,
    csrf_token: getNodeValue(nodes, 'csrf_token'),
    passkey_challenge: getNodeValue(nodes, 'passkey_challenge'),
  }
}

const formatSettingsFlow = (flow: KratosFlowResponse): KratosSettingsFlow => {
  const nodes = flow?.ui?.nodes || []

  return {
    id: flow.id,
    csrf_token: getNodeValue(nodes, 'csrf_token'),
    passkey_create_data: getNodeValue(nodes, 'passkey_create_data'),
    passkeys: parsePasskeys(nodes),
  }
}

export const fetchKratosSettingsFlow = async (flowId?: string | null): Promise<KratosSettingsFlow> => {
  // With a flowId, reuse the existing flow when Kratos still has it
  if (flowId) {
    try {
      const response = await kratos.get<KratosFlowResponse>('/self-service/settings/flows', {
        params: { id: flowId },
        headers: { Accept: 'application/json' },
      })
      return formatSettingsFlow(response.data)
    } catch (error) {
      const status = (error as AxiosError).response?.status
      // Only a client error means the flow itself is gone (expired, unknown, another
      // session) and safe to replace with a fresh one; a server/network failure says
      // nothing about the flow, so it must surface to the caller instead
      const isClientError = status !== undefined && status >= 400 && status < 500
      if (!isClientError) {
        throw error
      }
    }
  }

  const flow = await fetchKratosFlow('/self-service/settings/browser')
  return formatSettingsFlow(flow)
}

export const loginKratosPasskey = async (flowId: string, csrfToken: string, passkeyLogin: string) => {
  const response = await submitKratosFlow<KratosLoginResponse>('/self-service/login', flowId, {
    csrf_token: csrfToken,
    method: 'passkey',
    passkey_login: passkeyLogin,
  })

  return { email: response.data?.session?.identity?.traits?.email }
}

export const removeKratosPasskey = async (flowId: string, csrfToken: string, passkeyRemove: string) => {
  const response = await submitKratosFlow('/self-service/settings', flowId, {
    csrf_token: csrfToken,
    passkey_remove: passkeyRemove,
  })

  return response.status >= 200 && response.status < 300
}

export const registerKratosPasskey = async (flowId: string, csrfToken: string, passkeySettingsRegister: string) => {
  const response = await submitKratosFlow('/self-service/settings', flowId, {
    csrf_token: csrfToken,
    passkey_settings_register: passkeySettingsRegister,
  })

  return response.status >= 200 && response.status < 300
}
